const DB_NAME = 'pairferry';
const DB_VERSION = 2;
const SESSIONS = 'sessions';
const TRANSFERS = 'transfers';
const CONTACTS = 'contacts';
const PREPARED_PARCELS = 'preparedParcels';
const HISTORY = 'history';

export type ConflictPolicy = 'keep-both' | 'skip-existing' | 'replace' | 'ask';

export interface StoredSession {
  id: string;
  roomId: string;
  secret: string;
  role: 'host' | 'guest';
  peerUid?: string;
  createdAt: number;
  updatedAt: number;
}

export interface StoredFileCheckpoint {
  fileId: string;
  relativePath: string;
  verifiedOffset: number;
  size: number;
  lastModified: number;
  sourceHandle?: FileSystemFileHandle;
  destinationHandle?: FileSystemFileHandle;
}

export interface StoredTransfer {
  id: string;
  roomId: string;
  direction: 'outgoing' | 'incoming';
  state: 'offered' | 'active' | 'paused' | 'failed' | 'complete';
  directories?: string[];
  files: StoredFileCheckpoint[];
  createdAt: number;
  updatedAt: number;
}

export interface OneTimeReceive {
  destinationHandle: FileSystemDirectoryHandle;
  conflictPolicy: ConflictPolicy;
  expiresAt: number;
}

export interface StoredContact {
  id: string;
  name: string;
  role: 'host' | 'guest';
  secret: string;
  localIdentity: string;
  peerIdentity: string;
  createdAt: number;
  updatedAt: number;
  lastMetAt?: number;
  oneTimeReceive?: OneTimeReceive;
}

export interface StoredPreparedFile {
  fileId: string;
  relativePath: string;
  size: number;
  lastModified: number;
  type: string;
  sourceHandle: FileSystemFileHandle;
}

export interface StoredPreparedParcel {
  id: string;
  contactId: string;
  directories: string[];
  files: StoredPreparedFile[];
  totalBytes: number;
  createdAt: number;
  updatedAt: number;
}

export interface StoredTransferHistory {
  id: string;
  contactId?: string;
  peerLabel: string;
  direction: 'sending' | 'receiving';
  fileCount: number;
  totalBytes: number;
  completedAt: number;
  verified: true;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(SESSIONS)) database.createObjectStore(SESSIONS, { keyPath: 'id' });
      if (!database.objectStoreNames.contains(TRANSFERS)) database.createObjectStore(TRANSFERS, { keyPath: 'id' });
      if (!database.objectStoreNames.contains(CONTACTS)) database.createObjectStore(CONTACTS, { keyPath: 'id' });
      if (!database.objectStoreNames.contains(PREPARED_PARCELS)) database.createObjectStore(PREPARED_PARCELS, { keyPath: 'id' });
      if (!database.objectStoreNames.contains(HISTORY)) database.createObjectStore(HISTORY, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open local recovery storage.'));
  });
}

async function request<T>(storeName: string, mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, mode);
    const operation = action(transaction.objectStore(storeName));
    operation.onsuccess = () => resolve(operation.result);
    operation.onerror = () => reject(operation.error ?? new Error('Local recovery operation failed.'));
    transaction.oncomplete = () => database.close();
  });
}

export function saveSession(session: StoredSession): Promise<IDBValidKey> {
  return request(SESSIONS, 'readwrite', (store) => store.put(session));
}

export function listSessions(): Promise<StoredSession[]> {
  return request(SESSIONS, 'readonly', (store) => store.getAll());
}

export function deleteSession(id: string): Promise<undefined> {
  return request(SESSIONS, 'readwrite', (store) => store.delete(id) as IDBRequest<undefined>);
}

export function saveTransfer(transfer: StoredTransfer): Promise<IDBValidKey> {
  return request(TRANSFERS, 'readwrite', (store) => store.put(transfer));
}

export function getTransfer(id: string): Promise<StoredTransfer | undefined> {
  return request(TRANSFERS, 'readonly', (store) => store.get(id));
}

export function listTransfers(): Promise<StoredTransfer[]> {
  return request(TRANSFERS, 'readonly', (store) => store.getAll());
}

export function deleteTransfer(id: string): Promise<undefined> {
  return request(TRANSFERS, 'readwrite', (store) => store.delete(id) as IDBRequest<undefined>);
}

export function saveContact(contact: StoredContact): Promise<IDBValidKey> {
  return request(CONTACTS, 'readwrite', (store) => store.put(contact));
}

export function listContacts(): Promise<StoredContact[]> {
  return request(CONTACTS, 'readonly', (store) => store.getAll());
}

export function deleteContact(id: string): Promise<undefined> {
  return request(CONTACTS, 'readwrite', (store) => store.delete(id) as IDBRequest<undefined>);
}

export function savePreparedParcel(parcel: StoredPreparedParcel): Promise<IDBValidKey> {
  return request(PREPARED_PARCELS, 'readwrite', (store) => store.put(parcel));
}

export function listPreparedParcels(): Promise<StoredPreparedParcel[]> {
  return request(PREPARED_PARCELS, 'readonly', (store) => store.getAll());
}

export function deletePreparedParcel(id: string): Promise<undefined> {
  return request(PREPARED_PARCELS, 'readwrite', (store) => store.delete(id) as IDBRequest<undefined>);
}

export async function saveTransferHistory(entry: StoredTransferHistory, maximumEntries = 50): Promise<void> {
  await request(HISTORY, 'readwrite', (store) => store.put(entry));
  const entries = await listTransferHistory();
  const overflow = entries.sort((a, b) => b.completedAt - a.completedAt).slice(maximumEntries);
  await Promise.all(overflow.map((item) => deleteTransferHistory(item.id)));
}

export function listTransferHistory(): Promise<StoredTransferHistory[]> {
  return request(HISTORY, 'readonly', (store) => store.getAll());
}

export function deleteTransferHistory(id: string): Promise<undefined> {
  return request(HISTORY, 'readwrite', (store) => store.delete(id) as IDBRequest<undefined>);
}

export function clearTransferHistory(): Promise<undefined> {
  return request(HISTORY, 'readwrite', (store) => store.clear() as IDBRequest<undefined>);
}

export async function requestPersistentLocalStorage(): Promise<boolean> {
  return navigator.storage?.persist ? navigator.storage.persist() : false;
}
